import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import NotificationsDropdown from "@/components/navigation/NotificationsDropdown";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  HomeIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getInitials } from "@/utils/formatters";
import { API_BASE_URL } from "@/lib/axios";

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header = ({ toggleSidebar }: HeaderProps) => {
  const { t } = useTranslation();
  const { user, logoutUser, isInitialized } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Get company data for company admins
  const {
    data: companyData,
  } = useLoggedInCompany(user?.role === "company_admin");

  // Get user initials safely
  const getUserInitials = () => {
    return getInitials(user?.fullName || "");
  };

  // Get profile image URL
  const getProfileImageUrl = (): string | undefined => {
    if (user?.photo) {
      // If it's a data URL (base64), use it directly
      if (user.photo.startsWith("data:")) {
        return user.photo;
      }
      // If it's a filename, construct the API URL
      return `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/images/${user.photo}`;
    }
    return undefined;
  };

  // Get company logo URL for company admins
  const getCompanyLogoUrl = (): string | undefined => {
    const company = companyData?.data?.document;
    if (company?.logo) {
      // If it's a data URL (base64), use it directly
      if (company.logo.startsWith("data:")) {
        return company.logo;
      }
      // If it's a filename, construct the API URL
      return `${company.logo}`;
    }
    return undefined;
  };


  const handleLogout = async () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    try {
      await logoutUser();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Handler for close logout modal
  const handleCloseLogoutModal = () => {
    setShowLogoutModal(false);
  };

  return (
    <header className="z-10 bg-white shadow-sm dark:bg-gray-800">
      <div className="mx-auto flex h-16 max-w-full items-center justify-between px-4 md:px-6 lg:px-8">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-3 sm:p-2.5 md:p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 lg:hidden transition-all active:scale-95 touch-manipulation"
            aria-label="Toggle menu"
          >
            <Bars3Icon className="h-8 w-8 sm:h-7 sm:w-7 md:h-6 md:w-6" />
          </button>

          {/* Logo/Company Name - Links to Home */}
          <Link
            to="/"
            className="flex items-center space-x-1.5 sm:space-x-2 text-lg sm:text-xl font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900">
              <span className="text-xs sm:text-sm font-bold text-primary-700 dark:text-primary-300">
                G
              </span>
            </div>
            <span className="hidden sm:block">Gnzabe</span>
          </Link>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Home Button */}
          <Link
            to="/"
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 hidden sm:flex"
            aria-label={t("nav.home")}
          >
            <HomeIcon className="h-5 w-5" />
          </Link>

          <NotificationsDropdown />

          <LanguageSwitcher />

          <button
            onClick={toggleTheme}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 hidden sm:flex"
            aria-label={
              theme === "dark"
                ? t("settings.lightMode")
                : t("settings.darkMode")
            }
          >
            {theme === "dark" ? (
              <SunIcon className="h-5 w-5" />
            ) : (
              <MoonIcon className="h-5 w-5" />
            )}
          </button>

          {isInitialized && user && (
            <>
              <div className="relative">
                <Link to="/profile" className="flex items-center">
                  {user.role === "company_admin" ? (
                    // Company admin - show company logo
                    getCompanyLogoUrl() ? (
                      <img
                        src={getCompanyLogoUrl()}
                        alt="Company Logo"
                        className="h-8 w-8 rounded-full object-cover"
                        onError={(e) => {
                          // Fallback to building icon if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          target.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null
                  ) : // Regular user - show profile photo
                  getProfileImageUrl() ? (
                    <img
                      src={getProfileImageUrl()}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}

                  {/* Fallback to initials/icon */}
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 ${
                      (user.role === "company_admin" ? getCompanyLogoUrl() : getProfileImageUrl()) ? "hidden" : ""
                    }`}
                  >
                    {user.role === "company_admin" ? (
                      <BuildingOffice2Icon className="h-4 w-4" />
                    ) : (
                      <span className="text-sm font-medium">
                        {getUserInitials()}
                      </span>
                    )}
                  </div>

                  <span className="ml-2 hidden text-sm font-medium text-gray-700 dark:text-gray-300 md:block">
                    {user.role === "company_admin" ? (companyData?.data?.document?.name || user.name) : user.fullName}
                  </span>
                </Link>
              </div>

              <button
                onClick={handleLogout}
                className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 active:scale-95 transition-all"
                aria-label={t("nav.logout")}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 sm:h-5 sm:w-5" />
              </button>
            </>
          )}
        </div>
      </div>
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 w-full max-w-sm text-center animate-fadeIn">
            <h2 className="text-xl font-bold mb-4 text-primary-700 dark:text-primary-400">
              Confirm Logout
            </h2>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Are you sure you want to logout?
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="px-6 py-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 font-semibold"
                onClick={handleCloseLogoutModal}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 font-semibold shadow"
                onClick={confirmLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
